import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Tabs as MUITabs, Box, Stack, Tab } from "@mui/material";
import styles from "./styles";
import { colors } from "../../utils";

// gordon-0915-block-layout-tab-disabled-state-explainer: a category listed in
// `disabledCategories` (e.g. every block's "Layout" tab before it has
// content — VideoSettings/SocialLinksSettings/LinkWheelSettings) used to
// render pixel-identical to a normal inactive tab and swallow clicks
// silently. Shared with the CustomTabs sibling component's fix.
export const DISABLED_TAB_EXPLAINER =
  "Add content first - choose or upload media to unlock Layout";

const Tabs = ({
  categories,
  selectedCategory,
  onCategoryChange,
  color = "#81459B",
  indicatorColor = "#C283DA",
  tabStyle,
  disabledCategories = [],
  categoriesWithIcons = [],
  variant = "fullWidth",
  suffixStyle = {},
  sx = {},
}) => {
  const classes = styles(color, indicatorColor);

  // Which disabled category's explainer is currently shown, if any.
  const [explainerValue, setExplainerValue] = useState(null);

  // If the open category's disabled flag flips false (content was just
  // added), drop the stale explainer instead of leaving it stuck open under
  // a tab that now works normally.
  useEffect(() => {
    if (explainerValue !== null && !disabledCategories.includes(explainerValue)) {
      setExplainerValue(null);
    }
  }, [disabledCategories, explainerValue]);

  const disabledSx = (disabled) =>
    disabled ? { opacity: 0.5, cursor: "not-allowed" } : {};

  // A disabled tab must never activate its panel — the native MUI `disabled`
  // prop would achieve that, but it also blocks the click event entirely
  // (browsers/RTL don't fire click on disabled buttons), which would make it
  // impossible to show the explainer. So switching is blocked here instead,
  // leaving the Tab itself clickable.
  const handleCategoryChange = (event, newValue) => {
    if (disabledCategories.includes(newValue)) {
      return;
    }
    setExplainerValue(null);
    onCategoryChange(event, newValue);
  };

  const onTabClick = (value, disabled) => () => {
    if (!disabled) {
      return;
    }
    setExplainerValue((prev) => (prev === value ? null : value));
  };

  const renderTabs = () => {
    if (categoriesWithIcons.length > 0) {
      return categoriesWithIcons.map(
        ({ label, value, icon, suffix, customIcon }) => {
          const disabled = disabledCategories.includes(value);
          return (
          <Tab
            key={value}
            aria-disabled={disabled}
            onClick={onTabClick(value, disabled)}
            label={
              <Stack alignItems={"center"} gap={1} direction={"row"}>
                {/* <img style={{color:'red',fill:'red',stroke:'red'}} width={20} height={20} alt={icon} src={icon}></img> */}
                {!!icon &&
                  React.cloneElement(icon, {
                    width: 20,
                    height: 20,
                    fill:
                      selectedCategory === value
                        ? colors.PrimaryPurple
                        : colors.lightGrey,
                  })}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    fontFamily: "DM Sans",
                    fontSize: 14,
                    fontWeight: 400,
                    color:
                      selectedCategory === value
                        ? colors.PrimaryPurple
                        : colors.lightGrey,
                  }}
                >
                  {label}
                </div>
                {!!suffix && (
                  <div
                    style={{
                      borderRadius: 50,
                      padding: "2px 10px",
                      backgroundColor:
                        selectedCategory === value ? "#EFE9FE" : "#ECECEC",
                      ...suffixStyle,
                    }}
                  >
                    <div
                      style={{
                        alignItems: "center",
                        fontFamily: "DM Sans",
                        fontSize: 12,
                        color:
                          selectedCategory === value
                            ? colors.PrimaryPurple
                            : "inherit",
                      }}
                    >
                      {suffix}
                    </div>
                  </div>
                )}

                {!!customIcon && customIcon}
              </Stack>
            }
            value={value}
            sx={{
              ...classes.tabLabel,
              transition: "background-color 0.2s ease",
              "&:hover": disabled
                ? undefined
                : { backgroundColor: "#EFE9FE !important" },
            }}
            style={{
              ...tabStyle,
              ...disabledSx(disabled),
              transition: "background-color 0.2s ease",
              "&:hover": disabled
                ? undefined
                : { backgroundColor: "#EFE9FE !important" },
            }}
          />
          );
        }
      );
    }

    return categories.map((category) => {
      const disabled = disabledCategories.includes(category);
      return (
        <Tab
          key={category}
          label={category}
          value={category}
          aria-disabled={disabled}
          onClick={onTabClick(category, disabled)}
          sx={{
            ...classes.tabLabel,
            transition: "background-color 0.2s ease",
            "&:hover": disabled
              ? undefined
              : { backgroundColor: "#EFE9FE !important" },
          }}
          style={{
            ...tabStyle,
            ...disabledSx(disabled),
            transition: "background-color 0.2s ease",
            "&:hover": disabled
              ? undefined
              : { backgroundColor: "#EFE9FE !important" },
          }}
        />
      );
    });
  };

  return (
    <>
      <MUITabs
        value={selectedCategory}
        onChange={handleCategoryChange}
        variant={variant}
        TabIndicatorProps={{
          style: classes.tabContainer,
        }}
        sx={{
          minHeight: "auto",
          "& .MuiTab-root": {
            minWidth: "auto",
            padding: "8px 12px",
            fontSize: "13px",
            fontWeight: 500,
          },
          "& .MuiTabs-flexContainer": {
            gap: "4px",
          },
          ...sx,
        }}
      >
        {renderTabs()}
      </MUITabs>
      {/* Inline explainer for whichever disabled category was just
          clicked/tapped — role="status" + aria-live="polite" so screen
          readers announce it without an alert's assertive interruption.
          Absent entirely when nothing disabled has been clicked. */}
      {explainerValue !== null && (
        <Box
          role="status"
          aria-live="polite"
          sx={{
            fontSize: "13px",
            lineHeight: "18px",
            color: colors.textGrey,
            padding: "4px 4px 0",
          }}
        >
          {DISABLED_TAB_EXPLAINER}
        </Box>
      )}
    </>
  );
};

Tabs.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.string), // Array of category strings
  selectedCategory: PropTypes.string, // Currently selected category
  onCategoryChange: PropTypes.func.isRequired, // Function to handle tab change
  color: PropTypes.string, // Custom color for the tabs
  indicatorColor: PropTypes.string, // Custom indicator color
  tabStyle: PropTypes.object, // Style object for individual tabs
  disabledCategories: PropTypes.arrayOf(PropTypes.string), // Categories to disable
  categoriesWithIcons: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired, // Tab label
      value: PropTypes.string.isRequired, // Tab value
      icon: PropTypes.element, // Icon for the tab
    })
  ), // Array of category objects with icons
};

Tabs.defaultProps = {
  categories: [],
  selectedCategory: "",
  color: "#81459B",
  indicatorColor: "#C283DA",
  tabStyle: null,
  disabledCategories: [],
  categoriesWithIcons: [],
};

export default Tabs;
